"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Eye, Copy } from "lucide-react";
import { formatCurrency, formatDate, copyToClipboard } from "@/lib/utils";
import { Invoice, Customer, Vehicle, Appointment } from "@/types";
import { PaymentOutput } from "@/types/payment.types";
import { toast } from "sonner";

interface InvoicesTableProps {
  invoices: Invoice[];
  payments: PaymentOutput[];
  customers: Customer[];
  vehicles: Vehicle[];
  appointments: Appointment[];
  getStatusBadge: (status: string) => React.ReactElement;
  onView: (invoice: Invoice) => void;
  onDownload: (invoice: Invoice) => void;
  onViewPayment?: (payment: PaymentOutput) => void;
  onUpdatePaymentStatus?: (
    payment: PaymentOutput,
    newStatus: string,
  ) => Promise<void>;
  onDownloadReceipt?: (payment: PaymentOutput) => void;
}

export function InvoicesTable({
  invoices,
  payments,
  customers,
  vehicles,
  appointments,
  getStatusBadge,
  onView,
  onDownload,
  onViewPayment,
  onUpdatePaymentStatus,
  onDownloadReceipt,
}: InvoicesTableProps) {
  const getPaymentStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: "bg-yellow-500 hover:bg-yellow-600",
      COMPLETED: "bg-green-500 hover:bg-green-600",
      FAILED: "bg-red-500 hover:bg-red-600",
    };

    return (
      <Badge className={colors[status] || "bg-gray-500 hover:bg-gray-600"}>
        {status}
      </Badge>
    );
  };

  const getModeBadge = (mode: string) => {
    return (
      <Badge variant="outline" className="capitalize">
        {mode}
      </Badge>
    );
  };

  const handleSharePayment = async (payment: PaymentOutput) => {
    try {
      const receiptUrl = `${window.location.origin}/receipt/${payment.id}`;
      await copyToClipboard(receiptUrl);
      toast.success("Receipt link copied to clipboard!");
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("Failed to copy link to clipboard");
    }
  };

  const totalCount = invoices.length + payments.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices & Payments ({totalCount})</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>ID/Invoice #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Ref ID</TableHead>
              <TableHead>Service Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead>Payment Method</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {totalCount === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  className="text-center text-muted-foreground"
                >
                  No invoices or payments found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* Invoice rows */}
                {invoices.map((invoice) => {
                  const customer = customers.find(
                    (c) => c.id === invoice.customerId,
                  );
                  const vehicle = vehicles.find(
                    (v) => v.id === invoice.vehicleId,
                  );
                  const appointment = appointments.find(
                    (a) => a.id === invoice.appointmentId,
                  );

                  return (
                    <TableRow key={`invoice-${invoice.id}`}>
                      <TableCell>
                        <Badge variant="secondary">Invoice</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>{formatDate(invoice.date)}</TableCell>
                      <TableCell>{customer?.name || "N/A"}</TableCell>
                      <TableCell>
                        <span className="font-mono text-xs">
                          {appointment?.bookingRef ||
                            invoice.subscriptionId ||
                            "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {invoice.subscriptionId
                            ? "Subscription"
                            : appointment?.type === "USER_SUBSCRIPTION"
                              ? "Subscription"
                              : "One-Time"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(invoice.total)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(invoice.paymentStatus)}
                      </TableCell>
                      <TableCell className="capitalize">
                        {invoice.paymentMethod || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onView(invoice)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDownload(invoice)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Payment rows */}
                {payments.map((payment) => {
                  const paymentCustomer = payment.customer?.id
                    ? customers.find((c) => c.id === payment.customer?.id)
                    : null;

                  // Try to find associated appointment if refType is APPOINTMENT
                  const paymentAppointment =
                    payment.refType === "APPOINTMENT" && payment.refId
                      ? appointments.find((a) => a.id === payment.refId)
                      : null;

                  return (
                    <TableRow key={`payment-${payment.id}`}>
                      <TableCell>
                        <Badge variant="outline">Payment</Badge>
                      </TableCell>
                      <TableCell className="font-medium font-mono text-sm">
                        {payment.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell>
                        {payment.date
                          ? formatDate(new Date(payment.date))
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {paymentCustomer?.name ||
                          payment.customer?.name ||
                          "N/A"}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs">
                          {paymentAppointment?.bookingRef ||
                            (payment.refType === "APPOINTMENT"
                              ? "-"
                              : payment.refId || "-")}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{payment.refType}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {payment.discount > 0 ? (
                          <div className="flex flex-col">
                            <span>{formatCurrency(payment.dueAmount)}</span>
                            <span className="text-xs text-muted-foreground line-through">
                              {formatCurrency(payment.totalAmount)}
                            </span>
                          </div>
                        ) : (
                          formatCurrency(payment.totalAmount)
                        )}
                      </TableCell>
                      <TableCell>
                        {getPaymentStatusBadge(payment.status)}
                      </TableCell>
                      <TableCell>{getModeBadge(payment.mode)}</TableCell>
                      <TableCell className="text-right">
                        {onViewPayment ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onViewPayment(payment)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSharePayment(payment)}
                              title="Copy Receipt Link"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            {onDownloadReceipt && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => onDownloadReceipt(payment)}
                                title="Download Receipt"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
